import type { ProviderInstrumentConfig } from '@thinkalpha/locates-ws-client';
import type { ProviderInstruments } from 'src/admin/LAB/contracts/providers';

/** One `instruments` entry with its map key attached, for list rows and dialogs. */
export interface ProviderInstrumentRow extends ProviderInstrumentConfig {
    readonly symbol: string;
}

export interface InstrumentDraft {
    /** Free text; normalized with {@link normalizeInstrumentSymbol} when sent. */
    readonly symbol: string;
    readonly reason: string;
}

export interface InstrumentDialogState {
    readonly mode: 'add' | 'edit';
    /** The stored entry being edited; `null` in add mode. */
    readonly original: ProviderInstrumentRow | null;
    readonly draft: InstrumentDraft;
    /** `form` collects the entry; `confirm` shows what will be written before sending. */
    readonly step: 'form' | 'confirm';
    /** True while the write is in flight. */
    readonly isSaving: boolean;
    /** Identifies the in-flight write so a stale completion can't touch a newer dialog; `null` when idle. */
    readonly saveToken: number | null;
    /** Message of the last failed write; cleared when a new write starts. */
    readonly error: string | null;
}

export interface PendingInstrumentRemovalState {
    readonly entry: ProviderInstrumentRow;
    /** Identifies this confirmation so a stale completion can't clear a newer one. */
    readonly token: number;
    readonly isRemoving: boolean;
    readonly error: string | null;
}

export interface ProviderInstrumentsState {
    readonly providerId: string | null;
    readonly instruments: ProviderInstruments;
    readonly dialog: InstrumentDialogState | null;
    readonly pendingRemoval: PendingInstrumentRemovalState | null;
}

export const initialProviderInstrumentsState: ProviderInstrumentsState = {
    providerId: null,
    instruments: {},
    dialog: null,
    pendingRemoval: null,
};

export const EMPTY_INSTRUMENT_DRAFT: InstrumentDraft = {
    symbol: '',
    reason: '',
};

/** Matches the backend's instrument key: trimmed and upper-cased. */
export function normalizeInstrumentSymbol(symbol: string): string {
    return symbol.trim().toUpperCase();
}

/** Rows sorted by symbol so the list order is stable across writes. */
export function instrumentRows(instruments: ProviderInstruments): ProviderInstrumentRow[] {
    return Object.entries(instruments)
        .map(([symbol, entry]) => ({ ...entry, symbol }))
        .sort((a, b) => a.symbol.localeCompare(b.symbol));
}

/** The draft normalized for the API, or `null` when it is not ready to send. */
export function parseInstrumentDraft(dialog: InstrumentDialogState): { symbol: string; reason: string } | null {
    const symbol =
        dialog.mode === 'edit' && dialog.original
            ? dialog.original.symbol
            : normalizeInstrumentSymbol(dialog.draft.symbol);
    const reason = dialog.draft.reason.trim();
    if (!symbol || !reason) return null;
    // An edit that changes nothing has nothing to write.
    if (dialog.mode === 'edit' && dialog.original?.reason === reason) return null;
    return { symbol, reason };
}
